import { useMemo } from 'react';

import { withTooltip } from '@crossnokaye/ux-common';

import { ActionIconVariant as ActionIconVariantEnum } from '@repo/sdk/models/components';

import {
  isAtlasIcon,
  resolveIconComponent,
} from '@/features/equipment-browser/utils/resolve-icon-component';
import { Icon } from '@/ux-common/components/icon';
import { cn } from '@/ux-common/lib/utils';

type IconVariant = {
  color: string;
  backgroundColor?: string;
};

const ActionIconVariants: Record<string, IconVariant> = {
  [ActionIconVariantEnum.Default]: {
    color: 'stroke-foreground',
    backgroundColor: 'bg-input',
  },
  [ActionIconVariantEnum.EStop]: {
    color: 'stroke-foreground fill-foreground',
    backgroundColor: 'bg-input',
  },
  [ActionIconVariantEnum.On]: {
    color: 'stroke-primary-foreground',
    backgroundColor: 'bg-primary',
  },
  [ActionIconVariantEnum.Satisfied]: {
    color: 'stroke-primary-foreground',
    backgroundColor: 'bg-primary',
  },
  [ActionIconVariantEnum.Off]: {
    color: 'stroke-secondary-foreground',
    backgroundColor: 'bg-secondary',
  },
  [ActionIconVariantEnum.Critical]: {
    color: 'text-foreground',
    backgroundColor: 'bg-ckds-base-critical',
  },
  [ActionIconVariantEnum.Faulted]: {
    color: 'text-foreground',
    backgroundColor: 'bg-ckds-base-critical',
  },
  [ActionIconVariantEnum.Warning]: {
    color: 'text-background',
    backgroundColor: 'bg-ckds-base-warning',
  },
  [ActionIconVariantEnum.Caution]: {
    color: 'text-background',
    backgroundColor: 'bg-ckds-base-caution',
  },
  [ActionIconVariantEnum.Eai]: {
    color: 'stroke-none fill-white',
    backgroundColor: 'bg-conserving',
  },
  [ActionIconVariantEnum.ModeCaution]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-base-caution',
  },
  [ActionIconVariantEnum.ModeConserving]: {
    color: 'stroke-none fill-white',
    backgroundColor: 'bg-conserving',
  },
  [ActionIconVariantEnum.ModeCooling]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-cooling',
  },
  [ActionIconVariantEnum.ModeDefault]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-neutral-50',
  },
  [ActionIconVariantEnum.ModeSecondary]: {
    color: 'stroke-ckds-neutral-200',
    backgroundColor: 'bg-ckds-neutral-700',
  },
  [ActionIconVariantEnum.ModeTertiary]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-neutral-200',
  },
  [ActionIconVariantEnum.ModeWarning]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-base-warning',
  },
  [ActionIconVariantEnum.Oos]: {
    color: 'stroke-foreground',
    backgroundColor: 'bg-ckds-neutral-500',
  },
} as const;

type ActionIconVariant = keyof typeof ActionIconVariants;
type ActionIconSize = 'sm' | 'md' | 'lg' | 'xl';

// Map legacy size → new icon container + glyph classes:
//   sm = bare 16px glyph, no icon container
//   md = 24px icon (icon-sm) + 16px glyph (glyph-sm)
//   lg = 36px icon (icon-lg) + 24px glyph (glyph-lg)
//   xl = no Figma counterpart — kept on icon-lg/glyph-lg until call sites are reviewed
const SIZE_CLASSES: Record<ActionIconSize, { icon: string | null; glyph: string }> = {
  sm: { icon: 'icon-sm', glyph: 'glyph-sm' },
  md: { icon: 'icon-sm', glyph: 'glyph-sm' },
  lg: { icon: 'icon-lg', glyph: 'glyph-lg' },
  xl: { icon: 'icon-lg', glyph: 'glyph-lg' },
};

interface ActionIconProps {
  className?: string;
  'data-testid'?: string;
  fallbackIcon?: string;
  icon: string;
  isPulsing?: boolean;
  size?: ActionIconSize;
  tooltipContent?: string;
  variant?: ActionIconVariant;
}

const IconWithTooltip = withTooltip(Icon);

export const ActionIcon = ({
  className,
  fallbackIcon,
  icon,
  isPulsing,
  size = 'md',
  tooltipContent,
  variant = 'default',
  ...rest
}: ActionIconProps) => {
  const { color, backgroundColor } = ActionIconVariants[variant];
  const { icon: iconClass, glyph: glyphClass } = SIZE_CLASSES[size];
  const shouldFillGlyph = useMemo(() => isAtlasIcon(icon), [icon]);

  const IconComponent = resolveIconComponent({ fallbackIcon, icon });

  if (!IconComponent) {
    return null;
  }

  const glyphEl = (
    <IconComponent
      className={cn(shouldFillGlyph ? 'glyph-fill' : 'glyph', glyphClass, color)}
      aria-hidden="true"
    />
  );

  if (!iconClass) {
    return glyphEl;
  }

  const iconClassName = cn(iconClass, backgroundColor, isPulsing && 'icon-pulse', className);

  if (tooltipContent) {
    return (
      <IconWithTooltip
        className={iconClassName}
        contentClassName="whitespace-pre-line"
        delayDuration={100}
        tooltipContent={tooltipContent}
        {...rest}
      >
        {glyphEl}
      </IconWithTooltip>
    );
  }

  return (
    <Icon className={iconClassName} {...rest}>
      {glyphEl}
    </Icon>
  );
};
